import React from 'react';
import { useMemo } from 'react';
import { formatDate } from '../../../utils/dateUtils';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';

export const useColumns = (submissions = []) => {
  const { user } = useAuth();

  return useMemo(() => [
    {
      field: 'grading_service_name',
      headerName: 'TPG',
      width: 100,
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 200,
      renderCell: (item) => item.description || '-',
    },
    {
      field: 'coins',
      headerName: '# of Coins',
      width: 100,
      align: 'center',
      renderCell: (item) => {
        const coinCount = item.coins?.length || 0;
        const hasMismatch = item.coin_mismatch != null;
        
        return (
          <div className="flex items-center justify-center gap-1">
            <span>{coinCount}</span>
            {hasMismatch && (
              <span title="Coin count mismatch detected - click to view details">
                <svg className="w-4 h-4 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </span>
            )}
          </div>
        );
      },
    },
    {
      field: 'submission_number',
      headerName: 'Submission #',
      width: 150,
    },
    {
      field: 'order_number',
      headerName: 'Order #',
      width: 120,
      renderCell: (item) => {
        if (!item.order_number) return '-';
        return (
          <a 
            href={`https://www.pcgs.com/myaccount/order-details/${item.order_number}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:text-indigo-900"
            onClick={(e) => {
              e.stopPropagation();
              e._handled = true;
            }}
          >
            {item.order_number}
          </a>
        );
      },
    },
    {
      field: 'date_received',
      headerName: 'Date Received',
      width: 130,
      renderCell: (item) => item.date_received ? formatDate(item.date_received, { user }) : '-',
    },
    {
      field: 'service_type',
      headerName: 'Service Type',
      width: 130,
      renderCell: (item) => item.service_type || '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (item) => {
        const variant = item.status === 'Arrived' ? 'gray' :
                       item.status === 'Received' ? 'orange' :
                       item.status === 'Grading' ? 'blue' :
                       item.status === 'Encapsulation' ? 'yellow' :
                       item.status === 'Quality Check' ? 'indigo' :
                       item.status === 'Order Shipped' ? 'purple' :
                       item.status === 'Delivered' ? 'green' :
                       'gray';
        
        const colorMap = {
          gray: 'bg-gray-100 text-gray-800',
          orange: 'bg-orange-100 text-orange-800',
          blue: 'bg-blue-100 text-blue-800',
          yellow: 'bg-yellow-100 text-yellow-800',
          indigo: 'bg-indigo-100 text-indigo-800',
          purple: 'bg-purple-600 text-white',
          green: 'bg-green-100 text-green-800'
        };

        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorMap[variant]}`}>
            {item.status}
          </span>
        );
      },
    },
    {
      field: 'status_date',
      headerName: 'Status Date',
      width: 160,
      renderCell: (item) => item.status_date ? formatDate(item.status_date, { user, includeTime: true }) : '-',
    },
    {
      field: 'date_shipped',
      headerName: 'Date Shipped',
      width: 130,
      renderCell: (item) => item.date_shipped ? formatDate(item.date_shipped, { user }) : '-',
    },
    {
      field: 'grade_ready',
      headerName: 'Grades Ready',
      width: 120,
      align: 'center',
      renderCell: (item) => (
        <div className="inline-flex justify-center">
          {item.grade_ready ? (
            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
        </div>
      ),
    },
    {
      field: 'image_ready',
      headerName: 'Images Ready',
      width: 120,
      align: 'center',
      renderCell: (item) => (
        <div className="inline-flex justify-center">
          {item.image_ready ? (
            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
        </div>
      ),
    },
    {
      field: 'last_checked_at',
      headerName: 'Last Checked',
      width: 160,
      renderCell: (item) => item.last_checked_at ? formatDate(item.last_checked_at, { user, includeTime: true }) : '-',
    },
    {
      field: 'tracking_number',
      headerName: 'Tracking #',
      width: 140,
      renderCell: (item) => {
        if (!item.tracking_number) return '-';
        const match = item.tracking_number.match(/[?&]trknbr=([^&]+)/);
        const trackingNumber = match ? match[1] : item.tracking_number;
        
        if (!item.tracking_url) return trackingNumber;
        
        return (
          <a 
            href={item.tracking_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:text-indigo-900"
            onClick={(e) => {
              e.stopPropagation();
              e._handled = true;
            }}
          >
            {trackingNumber}
          </a>
        );
      },
    },
  ], [user]);
};
